import * as React from 'react';
import { Params, useParams } from 'react-router-dom';
import md5 from 'crypto-js/md5';
import hex from 'crypto-js/enc-hex';

import {
  CourseEnrollment,
  CourseUri,
  EnrollmentSummary,
  UserIdentifier,
} from '../../services/portal-core-data/portal-core-data.model';
import {
  PortalCoreDataService,
  useCoreDataService,
} from '../../services/portal-core-data/portal-core-data.service';
import { ErrorService, useErrorService } from '../../services/error.service';
import {
  CapClientInteractionService,
  useClientInteractionService,
} from '../../services/cap-client-interaction.service';
import { withHooks, clx, compare } from '../../shared/utils';
import { LoadingComponent } from '../../loading/loading.component';

type EnrollmentListProps = {
  coreData: PortalCoreDataService;
  errors: ErrorService;
  reportService: CapClientInteractionService;
  courseUri: CourseUri | undefined;
};

type EnrollmentListState = {
  loading: boolean;
  enrollment: CourseEnrollment;
};

class EnrollmentList extends React.Component<
  EnrollmentListProps,
  EnrollmentListState
> {
  state: EnrollmentListState = {
    loading: true,
    enrollment: { aggregatedEnrollment: [] },
  };

  componentDidMount() {
    this.onCourseChange();
  }

  componentDidUpdate(prevProps: Readonly<EnrollmentListProps>) {
    if (prevProps.courseUri !== this.props.courseUri) {
      this.onCourseChange();
    }
  }

  onCourseChange() {
    const { courseUri } = this.props;
    this.setState({
      loading: true,
    });

    if (courseUri && courseUri.length > 0) {
      this.props.coreData.loadCourseEnrollment(courseUri).then(
        (courseEnrollment) => {
          let enrollment = courseEnrollment;
          if (enrollment.aggregatedEnrollment) {
            enrollment.aggregatedEnrollment.sort((es1, es2) => {
              return (
                compare(es1.lastName, es2.lastName) ||
                compare(es1.firsName, es2.firsName)
              );
            });
          } else {
            enrollment.aggregatedEnrollment = [];
          }
          this.setState({
            loading: false,
            enrollment,
          });
        },
        (er) => {
          this.props.errors.handleError(er);
          this.setState({ loading: false });
        }
      );
    } else {
      this.setState({
        loading: false,
        enrollment: { aggregatedEnrollment: [] },
      });
    }
  }

  requestReport(summary: EnrollmentSummary): boolean {
    this.props.reportService.requestReport(
      this.props.courseUri!,
      summary.userIdentifier!,
      this.state.enrollment?.courseSummary?.shortLabel!,
      summary.firsName!,
      summary.lastName!
    );
    // wasUserRecentlyRequested will return new value
    this.forceUpdate();
    return false;
  }

  wasUserRecentlyRequested(userIdentifier: UserIdentifier): boolean {
    return this.props.reportService.wasUserRecentlyRequested(
      this.props.courseUri!,
      userIdentifier
    );
  }

  hashOf(userIdentifier: UserIdentifier): string {
    return hex.stringify(md5(userIdentifier));
  }

  render() {
    const { loading, enrollment } = this.state;

    return (
      <>
        <div className="row">
          <div className="col-auto">
            <h2 className="h3 mb-0 mt-0">Enrollment</h2>
            <p className="subdued small">Enrollment of selected course</p>
          </div>
        </div>

        {loading ? (
          <LoadingComponent />
        ) : (
          <div className="row row-cols-1">
            <div className="col mb-2">
              <div className="card d-flex flex-column h-100 p-3 shadow stat">
                <div className="card-body d-flex flex-column">
                  <div className="card-text">
                    <table className="table table-hover enrollment-listing">
                      <thead>
                        <tr>
                          <th scope="col">First Name</th>
                          <th scope="col">Last Name</th>
                          <th scope="col">Request Usage Log</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enrollment.aggregatedEnrollment!.map((listing) => (
                          <tr
                            key={this.hashOf(listing.userIdentifier!)}
                            id={`id-${this.hashOf(listing.userIdentifier!)}`}
                          >
                            <td>{listing.firsName!}</td>
                            <td>{listing.lastName!}</td>
                            <td>
                              <button
                                onClick={() => this.requestReport(listing)}
                                id={`report-${listing.userIdentifier}`}
                                className={clx('btn', {
                                  'btn-info': !this.wasUserRecentlyRequested(
                                    listing.userIdentifier!
                                  ),
                                  'btn-light': this.wasUserRecentlyRequested(
                                    listing.userIdentifier!
                                  ),
                                })}
                                disabled={this.wasUserRecentlyRequested(
                                  listing.userIdentifier!
                                )}
                                type="button"
                              >
                                {this.wasUserRecentlyRequested(
                                  listing.userIdentifier!
                                )
                                  ? '✓ Usage Log Requested'
                                  : 'Request Usage Log'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}

export const EnrollmentListComponent = withHooks<EnrollmentListProps, {}>(
  [useClientInteractionService, 'reportService'],
  [useErrorService, 'errors'],
  [useCoreDataService, 'coreData'],
  [
    useParams,
    (params) => ({
      courseUri: (params as Params).courseUri,
    }),
  ]
)(EnrollmentList);
